import { z } from "zod";
import { csvList, formatEnvIssues, positiveInt, requiredString } from "./env-fields";

// Browser-visible configuration (NEXT_PUBLIC_*, inlined at build). Every key is REQUIRED in .env.
const publicEnvSchema = z
  .object({
    NEXT_PUBLIC_LOCALES: csvList,
    NEXT_PUBLIC_RTL_LOCALES: csvList,
    NEXT_PUBLIC_DEFAULT_LOCALE: requiredString,
    NEXT_PUBLIC_LOCALE_PREFIX: z.enum(["always", "as-needed", "never"]),
    NEXT_PUBLIC_DEFAULT_THEME: requiredString,
    NEXT_PUBLIC_THEME_STORAGE_KEY: requiredString,
    NEXT_PUBLIC_MODE_STORAGE_KEY: requiredString,
    NEXT_PUBLIC_SETTINGS_COOKIE_MAX_AGE_SECONDS: positiveInt,
    NEXT_PUBLIC_SUPPORT_URL: z.string().url(),
    NEXT_PUBLIC_SHARE_DEBUGGER_URL: z.string().url(),
    NEXT_PUBLIC_TABS_STORAGE_PREFIX: requiredString,
    NEXT_PUBLIC_TABS_MAX: positiveInt,
    NEXT_PUBLIC_TOAST_TIMEOUT_MS: positiveInt,
    NEXT_PUBLIC_AUTOSAVE_INTERVAL_MS: positiveInt,
    NEXT_PUBLIC_TOAST_LIMIT: positiveInt,
    NEXT_PUBLIC_NOTIFICATION_SOUND_PATH: requiredString,
    NEXT_PUBLIC_NOTIFICATION_MUTE_KEY: requiredString,
    NEXT_PUBLIC_CONTACTS_COLUMNS_KEY: requiredString,
    NEXT_PUBLIC_PROVIDERS_COLUMNS_KEY: requiredString,
    NEXT_PUBLIC_VAPID_PUBLIC_KEY: requiredString,
  })
  .refine((v) => v.NEXT_PUBLIC_SUPPORT_URL.includes("{locale}"), { path: ["NEXT_PUBLIC_SUPPORT_URL"], message: "must contain {locale}" })
  .refine((v) => v.NEXT_PUBLIC_LOCALES.length > 0, { path: ["NEXT_PUBLIC_LOCALES"], message: "needs at least one locale" })
  .refine((v) => v.NEXT_PUBLIC_LOCALES.includes(v.NEXT_PUBLIC_DEFAULT_LOCALE), { path: ["NEXT_PUBLIC_DEFAULT_LOCALE"], message: "must be one of NEXT_PUBLIC_LOCALES" })
  .refine((v) => v.NEXT_PUBLIC_RTL_LOCALES.every((l) => v.NEXT_PUBLIC_LOCALES.includes(l)), { path: ["NEXT_PUBLIC_RTL_LOCALES"], message: "must be a subset of NEXT_PUBLIC_LOCALES" });

export type PublicEnv = z.infer<typeof publicEnvSchema>;

/** Validates the NEXT_PUBLIC_* values. Throws a message listing every bad key. */
export function parsePublicEnv(source: Record<string, string | undefined>): PublicEnv {
  const result = publicEnvSchema.safeParse(source);
  if (!result.success) throw new Error(`Invalid .env (public keys):\n${formatEnvIssues(result.error)}`);
  return result.data;
}

// Next.js only inlines NEXT_PUBLIC_* when each key is referenced literally, so they are listed one by one.
export function getPublicEnv(): PublicEnv {
  return parsePublicEnv({
    NEXT_PUBLIC_LOCALES: process.env.NEXT_PUBLIC_LOCALES,
    NEXT_PUBLIC_RTL_LOCALES: process.env.NEXT_PUBLIC_RTL_LOCALES,
    NEXT_PUBLIC_LOCALE_PREFIX: process.env.NEXT_PUBLIC_LOCALE_PREFIX,
    NEXT_PUBLIC_DEFAULT_LOCALE: process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
    NEXT_PUBLIC_DEFAULT_THEME: process.env.NEXT_PUBLIC_DEFAULT_THEME,
    NEXT_PUBLIC_THEME_STORAGE_KEY: process.env.NEXT_PUBLIC_THEME_STORAGE_KEY,
    NEXT_PUBLIC_MODE_STORAGE_KEY: process.env.NEXT_PUBLIC_MODE_STORAGE_KEY,
    NEXT_PUBLIC_SETTINGS_COOKIE_MAX_AGE_SECONDS: process.env.NEXT_PUBLIC_SETTINGS_COOKIE_MAX_AGE_SECONDS,
    NEXT_PUBLIC_SUPPORT_URL: process.env.NEXT_PUBLIC_SUPPORT_URL,
    NEXT_PUBLIC_SHARE_DEBUGGER_URL: process.env.NEXT_PUBLIC_SHARE_DEBUGGER_URL,
    NEXT_PUBLIC_TABS_STORAGE_PREFIX: process.env.NEXT_PUBLIC_TABS_STORAGE_PREFIX,
    NEXT_PUBLIC_TABS_MAX: process.env.NEXT_PUBLIC_TABS_MAX,
    NEXT_PUBLIC_TOAST_TIMEOUT_MS: process.env.NEXT_PUBLIC_TOAST_TIMEOUT_MS,
    NEXT_PUBLIC_AUTOSAVE_INTERVAL_MS: process.env.NEXT_PUBLIC_AUTOSAVE_INTERVAL_MS,
    NEXT_PUBLIC_TOAST_LIMIT: process.env.NEXT_PUBLIC_TOAST_LIMIT,
    NEXT_PUBLIC_NOTIFICATION_SOUND_PATH: process.env.NEXT_PUBLIC_NOTIFICATION_SOUND_PATH,
    NEXT_PUBLIC_NOTIFICATION_MUTE_KEY: process.env.NEXT_PUBLIC_NOTIFICATION_MUTE_KEY,
    NEXT_PUBLIC_CONTACTS_COLUMNS_KEY: process.env.NEXT_PUBLIC_CONTACTS_COLUMNS_KEY,
    NEXT_PUBLIC_PROVIDERS_COLUMNS_KEY: process.env.NEXT_PUBLIC_PROVIDERS_COLUMNS_KEY,
    NEXT_PUBLIC_VAPID_PUBLIC_KEY: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
  });
}

/** The address of the support page in the reader's language (the setting holds it with `{locale}` where the language goes). */
export function supportUrl(locale: string): string {
  return getPublicEnv().NEXT_PUBLIC_SUPPORT_URL.replace("{locale}", locale);
}
